/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { PlatformList } from '@/components/PlatformList'
import { TPlatformListService } from '@/components/PlatformList/interface'
import { useTimeFilter } from '@/hooks/useTimeFilter'
import { useTranslateAmount } from '@/hooks/useTranslateAmount'
import { LayoutOthers } from '@/layouts/LayoutOthers'
import { StatementTrend } from '@/pages/billing/Statements/StatementTrend'
import { useStatementsColumns } from '@/pages/billing/Statements/useStatementsColumns'
import { accountClient } from '@/services/cloudClient/accountClient'
import { createOrders } from '@/utils/createOrders'
import { ModelAvailableBalance } from 'account-client'
import { Col, Row, Space } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import styles from './index.less'

export function Statements() {
  const { t } = useTranslation()
  const { contractId } = useParams()
  const { currency } = useTranslateAmount()
  const [availableBalance, setAvailableBalance] = useState<ModelAvailableBalance>()
  const { filter: timeFilter, time } = useTimeFilter(null, {
    format: 'YYYY-MM',
    picker: 'month',
    placeholder: t('Select Billing Date'),
    style: { width: 180 },
    allowClear: true,
  })
  const columns = useStatementsColumns()
  const emitter = PlatformList.useCreateEmitter()

  const service: TPlatformListService = ({ pagination, sorter }, options) => {
    const { current, pageSize } = pagination || {}
    return accountClient.statementApi.listStatements(
      contractId,
      {
        pageNum: current,
        pageSize,
        start: time?.startOf('month').valueOf(),
        end: time?.add(1, 'months').startOf('month').valueOf(),
        ...createOrders(sorter),
      },
      options,
    )
  }

  useEffect(() => {
    emitter.fire({
      pagination: {
        current: 1,
        pageSize: 10,
      },
    })
  }, [time])

  useEffect(() => {
    accountClient.availableBalanceApi.getAvailableBalance(contractId).then(res => setAvailableBalance(res.data))
  }, [])

  return (
    <LayoutOthers.MainContent title={t('Billing')}>
      <LayoutOthers.Alert className="margin-bottom-20">
        <div>· {t('statements_desc_1')}</div>
        <div>· {t('statements_desc_2')}</div>
        <div>· {t('statements_desc_3')}</div>
      </LayoutOthers.Alert>
      <Row gutter={20} className="margin-bottom-20">
        <Col span={6}>
          <LayoutOthers.ChartBox title={t('Unbilled Amount')}>
            <div className={styles.unbilledAmount}>
              <div className={styles.amount}>
                {availableBalance?.details.unbilledFormatted}
                <span className="small-currency"> {currency}</span>
              </div>
              <div className="light-label">
                {t('Statement Date')}: {t('2nd of each month')}
              </div>
            </div>
          </LayoutOthers.ChartBox>
        </Col>
        <Col span={18}>
          <StatementTrend />
        </Col>
      </Row>
      <PlatformList emitter={emitter} service={service} columns={columns} createTitle={() => <Space>{timeFilter}</Space>} />
    </LayoutOthers.MainContent>
  )
}
